import React from 'react';
import { Link } from 'react-router-dom';
import { aboutContent, stats } from '../mock';
import { Target, Eye, Award, Users, CheckCircle } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen" style={{ background: '#ECEC75' }}>
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-6xl font-bold text-black mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            About Netex Digital
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto">
            Your trusted partner in digital transformation and growth
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div 
              className="p-12 rounded-xl shadow-lg"
              style={{ background: '#e6e67c' }}
            >
              <Target size={48} className="text-black mb-6" />
              <h2 
                className="text-3xl md:text-4xl font-bold text-black mb-6"
                style={{ fontFamily: 'Crimson Text, serif' }}
              >
                Our Mission
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {aboutContent.mission}
              </p>
            </div>
            <div 
              className="p-12 rounded-xl shadow-lg"
              style={{ background: '#e6e67c' }}
            >
              <Eye size={48} className="text-black mb-6" />
              <h2 
                className="text-3xl md:text-4xl font-bold text-black mb-6"
                style={{ fontFamily: 'Crimson Text, serif' }}
              >
                Our Vision
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                {aboutContent.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: '#e6e67c' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl md:text-5xl font-bold text-black mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              Our Impact
            </h2>
            <p className="text-xl text-gray-700">
              Numbers that showcase our commitment to excellence
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-md">
                <div className="text-5xl font-bold text-black mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {stat.value}
                </div>
                <div className="text-gray-700 font-medium text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Award size={56} className="text-black mx-auto mb-6" />
            <h2 
              className="text-4xl md:text-5xl font-bold text-black mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              Our Core Values
            </h2>
            <p className="text-xl text-gray-700">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {aboutContent.values.map((value, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl shadow-md flex items-center gap-4"
                style={{ background: '#e6e67c' }}
              >
                <CheckCircle className="text-black flex-shrink-0" size={28} />
                <span className="text-lg text-gray-700 font-medium">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{ background: '#e6e67c' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Users size={56} className="text-black mx-auto mb-6" />
            <h2 
              className="text-4xl md:text-5xl font-bold text-black mb-4"
              style={{ fontFamily: 'Crimson Text, serif' }}
            >
              Expert Team, Exceptional Results
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Our diverse team of digital marketing specialists brings together decades 
              of combined experience in SEO, content creation, social media, and web design. 
              We're passionate about helping businesses succeed in the digital landscape.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { title: 'Certified Experts', description: 'Our team holds industry certifications from Google, Facebook, and HubSpot' },
              { title: 'Continuous Learning', description: 'We stay ahead of trends through ongoing training and education' },
              { title: 'Client-Focused', description: 'Your success is our success. We treat your business like our own' }
            ].map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-md text-center">
                <h3 className="text-2xl font-bold text-black mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl md:text-5xl font-bold text-black mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}
          >
            Ready to Work Together?
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Let's discuss how Netex Digital can help your business thrive in the digital world.
          </p>
          <Link to="/contact">
            <button className="bg-black text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-gray-800 transition-all hover:translate-y-[-2px] shadow-lg">
              Get in Touch
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};